import { LinkOutlined, SearchOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { Input } from 'antd';
import React from 'react';
import { OpenAPI } from '@/client';
import type { UserPublic } from '@/client/types.gen';
import {
  AdminRegionSwitcher,
  AvatarDropdown,
  AvatarName,
  CommandPalette,
  CurrentGroupBadge,
  CurrentTenantCacheClearButton,
  Footer,
  GlobalPendingFeedbackAlert,
  GlobalToolbox,
  ProductSwitcher,
  Question,
  SelectLang,
} from '@/components';
import { adminUsersFacade } from '@/facades/systemAdmin';
import {
  getAdminTenant,
  installAdminTenantFetchInterceptor,
} from '@/utils/adminTenant';
import {
  installPermissionPreviewFetchInterceptor,
  resolvePermissionPreview,
} from '@/utils/permissionPreview';
import { getToken, removeToken } from '@/utils/token';
import { initializeAdminRegion } from '@/utils/adminRegion';
import defaultSettings from '../config/defaultSettings';
import {
  commandPaletteShortcutText,
  openCommandPalette,
} from './utils/commandPalette';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

// 根据当前后台环境和所选区域配置管理 API 地址。
initializeAdminRegion();
OpenAPI.TOKEN = async () => getToken() || '';
OpenAPI.HEADERS = async (): Promise<Record<string, string>> => {
  const tenant = getAdminTenant();
  const headers: Record<string, string> = {};
  if (tenant) headers['X-Admin-Tenant'] = tenant;
  return headers;
};
installAdminTenantFetchInterceptor();
installPermissionPreviewFetchInterceptor();

type CurrentUser = UserPublic;

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: CurrentUser;
  baseCurrentUser?: CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<CurrentUser | undefined>;
}> {
  OpenAPI.TOKEN = async () => getToken() || '';

  const readCurrentUser = async () => {
    try {
      return await adminUsersFacade.readUserMe();
    } catch (_error) {
      // 获取用户信息失败，清除 token 并跳转到登录页
      removeToken();
      OpenAPI.TOKEN = async () => getToken() || '';
      if (history.location.pathname !== loginPath) {
        history.push(loginPath);
      }
    }
    return undefined;
  };

  const fetchUserInfo = async () => {
    return readCurrentUser();
  };

  // 如果不是登录页面，执行获取用户信息
  const { location } = history;
  const isPublicReaderBugReport = location.pathname.startsWith(
    '/maintenance/reader-bugs/',
  );
  if (
    !isPublicReaderBugReport &&
    ![
      loginPath,
      '/user/select-product',
      '/user/register',
      '/user/register-result',
      '/reader-feedback/preview',
    ].includes(location.pathname)
  ) {
    const baseCurrentUser = await readCurrentUser();
    const currentUser = await resolvePermissionPreview(baseCurrentUser);
    return {
      fetchUserInfo,
      currentUser,
      baseCurrentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const shortcutText = commandPaletteShortcutText();
  return {
    actionsRender: () => [
      <ProductSwitcher key="productSwitcher" />,
      <AdminRegionSwitcher key="adminRegionSwitcher" />,
      <CurrentTenantCacheClearButton key="tenantCacheClear" />,
      <CurrentGroupBadge key="currentGroup" />,
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    menuExtraRender: () =>
      initialState?.currentUser ? (
        <div
          style={{
            padding: '8px 12px 10px',
            borderBlockEnd: '1px solid rgba(5, 5, 5, 0.06)',
          }}
        >
          <Input
            readOnly
            placeholder="搜索菜单 / Tab / 拼音 / 首字母"
            style={{ width: '100%' }}
            prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
            suffix={
              <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                {shortcutText}
              </span>
            }
            styles={{
              input: {
                fontSize: 13,
                cursor: 'pointer',
              },
            }}
            onClick={openCommandPalette}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openCommandPalette();
              }
            }}
          />
        </div>
      ) : null,
    avatarProps: {
      src:
        initialState?.currentUser?.username || initialState?.currentUser?.email
          ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${initialState.currentUser.username || initialState.currentUser.email}`
          : undefined,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: (() => {
        const account =
          initialState?.currentUser?.username ||
          initialState?.currentUser?.email;
        const tenant = getAdminTenant();
        return account ? `${tenant || 'foresee'} ${account}` : undefined;
      })(),
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <a
            key="openapi"
            href="https://api-test-us.gemtoonou.com/admin/docs"
            target="_blank"
            rel="noopener noreferrer"
          >
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </a>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {initialState?.currentUser && <GlobalPendingFeedbackAlert />}
          {children}
          {initialState?.currentUser && <CommandPalette />}
          {initialState?.currentUser && <GlobalToolbox />}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};
